import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { roleLabel } from "../../platform/locale";
import "./AdminOrganizationERP.css";

type OrganizationRow = {
  organizationId:string;
  type:string;
  code?:string|null;
  name:string;
  status:string;
  parentOrganizationId?:string|null;
  parentName?:string|null;
  memberCount:number;
  learnerCount:number;
};

type RoleAssignment={
  roleAssignmentId:string;
  roleKey:string;
  active:boolean;
};

type Membership={
  membershipId:string;
  displayName:string;
  email?:string|null;
  identityLinked:boolean;
  status:string;
  roles:RoleAssignment[];
};

type Invitation={
  invitationId:string;
  expectedEmail?:string|null;
  roleKeys:string[];
  status:string;
  expiresAt:string;
};

type AccessDirectory={
  organization:{
    organizationId:string;
    name:string;
    type:string;
    status:string;
    parentOrganizationId?:string|null;
  };
  memberships:Membership[];
  invitations:Invitation[];
  learners:Array<{learnerId:string;displayName:string}>;
};

function typeLabel(type:string){
  if(type==="INSTITUTION") return "기관";
  if(type==="EDUCATION_OFFICE") return "교육청";
  if(type==="SCHOOL") return "학교";
  if(type==="PLATFORM") return "플랫폼";
  if(type==="PROVIDER") return "제공기관";
  return type;
}

function navigateWithOrganization(path:string,organizationId:string){
  const url=organizationId
    ? `${path}?organization=${encodeURIComponent(organizationId)}`
    : path;
  window.history.pushState({}, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function AdminOrganizationsPage({client}:{client:PlatformClient}){
  const [rows,setRows]=useState<OrganizationRow[]>([]);
  const [selectedId,setSelectedId]=useState("");
  const [directory,setDirectory]=useState<AccessDirectory|null>(null);
  const [query,setQuery]=useState("");
  const [filterType,setFilterType]=useState("ALL");
  const [showCreate,setShowCreate]=useState(false);
  const [type,setType]=useState("INSTITUTION");
  const [code,setCode]=useState("");
  const [name,setName]=useState("");
  const [parentId,setParentId]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    client.rpc("admin_organization_directory").then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=(data || []) as OrganizationRow[];
      setRows(next);
      const requested=new URLSearchParams(window.location.search).get("organization") || "";
      setSelectedId((current)=>
        requested && next.some((item)=>item.organizationId===requested)
          ? requested
          : next.some((item)=>item.organizationId===current)
            ? current
            : next.find((item)=>item.status==="ACTIVE" && item.type==="SCHOOL")?.organizationId
              || next.find((item)=>item.status==="ACTIVE")?.organizationId
              || next[0]?.organizationId
              || ""
      );
    });
    return()=>{active=false;};
  },[client,refresh]);

  useEffect(()=>{
    if(!selectedId){setDirectory(null);return;}
    let active=true;
    client.rpc("admin_access_directory",{p_organization_id:selectedId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setDirectory(data as AccessDirectory);
    });
    return()=>{active=false;};
  },[client,selectedId,refresh]);

  const parentOptions=useMemo(
    ()=>rows.filter((item)=>item.status==="ACTIVE" && ["INSTITUTION","EDUCATION_OFFICE"].includes(item.type)),
    [rows]
  );

  const selected=useMemo(
    ()=>rows.find((item)=>item.organizationId===selectedId) || null,
    [rows,selectedId]
  );

  const visible=useMemo(()=>{
    const keyword=query.trim().toLowerCase();
    return rows
      .filter((item)=>filterType==="ALL" || item.type===filterType)
      .filter((item)=>!keyword || [item.name,item.code || "",item.parentName || ""].some((value)=>value.toLowerCase().includes(keyword)))
      .sort((a,b)=>{
        if(a.status!==b.status) return a.status==="ACTIVE" ? -1 : 1;
        if(a.type!==b.type) return a.type.localeCompare(b.type);
        return a.name.localeCompare(b.name,"ko");
      });
  },[rows,query,filterType]);

  const children=useMemo(
    ()=>selected ? rows.filter((item)=>item.parentOrganizationId===selected.organizationId) : [],
    [rows,selected]
  );

  const stats=useMemo(()=>{
    const memberships=directory?.memberships || [];
    const invitations=directory?.invitations || [];
    return {
      activeMembers:memberships.filter((item)=>item.status==="ACTIVE").length,
      linkedAccounts:memberships.filter((item)=>item.status==="ACTIVE" && item.identityLinked).length,
      unlinkedAccounts:memberships.filter((item)=>item.status==="ACTIVE" && !item.identityLinked).length,
      openInvites:invitations.filter((item)=>item.status==="OPEN").length,
      activeRoles:memberships.reduce((sum,item)=>sum+item.roles.filter((role)=>role.active).length,0)
    };
  },[directory]);

  async function create(){
    setBusy(true);
    setError("");
    const {error}=await client.rpc("admin_create_organization",{
      p_organization_type:type,
      p_code:code.trim() || null,
      p_name:name.trim(),
      p_parent_organization_id:type==="SCHOOL" ? (parentId || null) : null
    });
    setBusy(false);
    if(error){setError(error.message);return;}
    setCode("");
    setName("");
    setParentId("");
    setShowCreate(false);
    setRefresh((value)=>value+1);
  }

  return (
    <section className="org-erp-page">
      <header className="org-erp-hero">
        <div>
          <p className="eyebrow">ORGANIZATION ERP · MASTER DATA</p>
          <h2>기관·학교 운영관리</h2>
          <p>조직 구조와 소속 계정, 학생 규모, 초대·권한 상태를 기관 단위로 함께 봅니다.</p>
        </div>
        <button className="primary" onClick={()=>setShowCreate((value)=>!value)}>
          {showCreate ? "등록 닫기" : "+ 기관·학교 등록"}
        </button>
      </header>

      {error && <p className="error">{error}</p>}

      <div className="org-erp-kpis">
        <OrgKpi label="전체 조직" value={rows.length} />
        <OrgKpi label="학교" value={rows.filter((item)=>item.type==="SCHOOL").length} />
        <OrgKpi label="등록 학생" value={rows.reduce((sum,item)=>sum+item.learnerCount,0)} />
        <OrgKpi label="등록 구성원" value={rows.reduce((sum,item)=>sum+item.memberCount,0)} />
      </div>

      {showCreate && (
        <section className="org-erp-create">
          <div className="org-erp-section-head">
            <div><p className="eyebrow">NEW ORGANIZATION</p><h3>기관 또는 학교 등록</h3></div>
            <span>Canonical Organization</span>
          </div>
          <div className="org-create-grid">
            <label>유형
              <select value={type} onChange={(event)=>setType(event.target.value)}>
                <option value="INSTITUTION">기관</option>
                <option value="EDUCATION_OFFICE">교육청</option>
                <option value="SCHOOL">학교</option>
              </select>
            </label>
            <label>코드
              <input value={code} onChange={(event)=>setCode(event.target.value)} placeholder="예: RS-SEOUL-001" />
            </label>
            <label className="wide">이름
              <input value={name} onChange={(event)=>setName(event.target.value)} placeholder="기관 또는 학교 이름" />
            </label>
            {type==="SCHOOL" && (
              <label className="wide">상위 기관
                <select value={parentId} onChange={(event)=>setParentId(event.target.value)}>
                  <option value="">상위 기관 선택</option>
                  {parentOptions.map((item)=>(
                    <option key={item.organizationId} value={item.organizationId}>{item.name} · {typeLabel(item.type)}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="form-actions">
            <button className="primary compact-button" disabled={busy || !name.trim() || (type==="SCHOOL" && !parentId)} onClick={create}>
              {busy ? "등록 중…" : "Organization 등록"}
            </button>
          </div>
        </section>
      )}

      <div className="org-erp-workspace">
        <aside className="org-erp-directory">
          <div className="org-erp-directory-tools">
            <input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="기관·학교 검색" />
            <select value={filterType} onChange={(event)=>setFilterType(event.target.value)}>
              <option value="ALL">전체 유형</option>
              <option value="SCHOOL">학교</option>
              <option value="EDUCATION_OFFICE">교육청</option>
              <option value="INSTITUTION">기관</option>
              <option value="PLATFORM">플랫폼</option>
            </select>
          </div>
          <div className="org-erp-directory-count">{visible.length}개 조직</div>
          <div className="org-erp-org-list">
            {visible.map((item)=>(
              <button
                key={item.organizationId}
                className={selectedId===item.organizationId ? "active" : ""}
                onClick={()=>setSelectedId(item.organizationId)}
              >
                <div>
                  <span className={`org-erp-type ${item.type.toLowerCase()}`}>{typeLabel(item.type)}</span>
                  <span className={item.status==="ACTIVE" ? "org-erp-live" : "org-erp-off"}>{item.status==="ACTIVE" ? "운영" : item.status}</span>
                </div>
                <strong>{item.name}</strong>
                <small>{item.code || "코드 없음"}{item.parentName ? ` · ${item.parentName}` : ""}</small>
                <footer><span>구성원 {item.memberCount}</span><span>학생 {item.learnerCount}</span></footer>
              </button>
            ))}
          </div>
        </aside>

        <main className="org-erp-detail">
          {selected ? (
            <>
              <header className="org-erp-detail-head">
                <div>
                  <div className="org-erp-detail-meta">
                    <span className={`org-erp-type ${selected.type.toLowerCase()}`}>{typeLabel(selected.type)}</span>
                    <span className={selected.status==="ACTIVE" ? "status-pill good" : "status-pill"}>{selected.status==="ACTIVE" ? "활성" : selected.status}</span>
                  </div>
                  <h2>{selected.name}</h2>
                  <p>{selected.code || "기관 코드 없음"}{selected.parentName ? ` · 상위 ${selected.parentName}` : " · 최상위 조직"}</p>
                </div>
                <div className="org-erp-head-actions">
                  <button className="secondary" onClick={()=>navigateWithOrganization("/access",selected.organizationId)}>사용자·권한 관리</button>
                </div>
              </header>

              <div className="org-erp-detail-kpis">
                <OrgDetailKpi label="활성 구성원" value={stats.activeMembers} />
                <OrgDetailKpi label="로그인 연결" value={stats.linkedAccounts} alert={stats.unlinkedAccounts>0 ? `${stats.unlinkedAccounts}명 미연결` : undefined} />
                <OrgDetailKpi label="활성 역할" value={stats.activeRoles} />
                <OrgDetailKpi label="열린 초대" value={stats.openInvites} alert={stats.openInvites>0 ? "회수 여부 확인" : undefined} />
                <OrgDetailKpi label="학생" value={selected.learnerCount} />
                <OrgDetailKpi label="하위 조직" value={children.length} />
              </div>

              <div className="org-erp-detail-columns">
                <section className="org-erp-panel">
                  <div className="org-erp-section-head">
                    <div><p className="eyebrow">PEOPLE</p><h3>구성원 현황</h3></div>
                    <button className="text-button" onClick={()=>navigateWithOrganization("/access",selected.organizationId)}>전체 관리 →</button>
                  </div>
                  <div className="org-erp-people-list">
                    {(directory?.memberships || []).slice(0,8).map((member)=>(
                      <div key={member.membershipId}>
                        <span className={member.identityLinked ? "org-erp-account-dot linked" : "org-erp-account-dot"} />
                        <div>
                          <strong>{member.displayName}</strong>
                          <small>{member.email || "로그인 이메일 미연결"}</small>
                        </div>
                        <span>{member.roles.filter((role)=>role.active).map((role)=>roleLabel(role.roleKey)).join(", ") || "역할 없음"}</span>
                      </div>
                    ))}
                    {!directory?.memberships.length && <p className="case-empty">등록된 구성원이 없습니다.</p>}
                  </div>
                </section>

                <section className="org-erp-panel">
                  <div className="org-erp-section-head">
                    <div><p className="eyebrow">STRUCTURE</p><h3>하위 조직</h3></div>
                    <span>{children.length}개</span>
                  </div>
                  <div className="org-erp-child-list">
                    {children.map((child)=>(
                      <button key={child.organizationId} onClick={()=>setSelectedId(child.organizationId)}>
                        <div><strong>{child.name}</strong><small>{child.code || "코드 없음"}</small></div>
                        <span>{child.memberCount}명 · 학생 {child.learnerCount}</span>
                      </button>
                    ))}
                    {!children.length && <p className="case-empty">하위 조직이 없습니다.</p>}
                  </div>
                </section>
              </div>

              <section className="org-erp-panel">
                <div className="org-erp-section-head">
                  <div><p className="eyebrow">ACCESS HEALTH</p><h3>계정·권한 상태</h3></div>
                  <span>운영 점검</span>
                </div>
                <div className="org-erp-health-grid">
                  <HealthItem
                    label="Google/계정 연결"
                    status={stats.unlinkedAccounts===0 ? "GOOD" : "ATTENTION"}
                    detail={stats.unlinkedAccounts===0 ? "활성 구성원 계정 연결 정상" : `${stats.unlinkedAccounts}명의 활성 구성원이 로그인 계정과 연결되지 않았습니다.`}
                  />
                  <HealthItem
                    label="미수령 초대"
                    status={stats.openInvites===0 ? "GOOD" : "ATTENTION"}
                    detail={stats.openInvites===0 ? "대기 중인 직원 초대 없음" : `${stats.openInvites}개의 초대가 아직 Claim되지 않았습니다.`}
                  />
                  <HealthItem
                    label="조직 구조"
                    status={selected.type==="SCHOOL" && !selected.parentOrganizationId ? "ATTENTION" : "GOOD"}
                    detail={selected.type==="SCHOOL" && !selected.parentOrganizationId ? "학교의 상위 기관이 연결되지 않았습니다." : "조직 상하 관계 정상"}
                  />
                </div>
              </section>
            </>
          ) : (
            <p className="case-empty">관리할 기관 또는 학교를 선택하세요.</p>
          )}
        </main>
      </div>
    </section>
  );
}

function OrgKpi({label,value}:{label:string;value:number}){
  return <div className="org-erp-kpi"><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}

function OrgDetailKpi({label,value,alert}:{label:string;value:number;alert?:string}){
  return (
    <div className="org-erp-detail-kpi">
      <span>{label}</span>
      <strong>{value.toLocaleString("ko-KR")}</strong>
      <small className={alert ? "attention" : ""}>{alert || "정상"}</small>
    </div>
  );
}

function HealthItem({label,status,detail}:{label:string;status:"GOOD"|"ATTENTION";detail:string}){
  return (
    <div className="org-erp-health-item">
      <span className={status==="GOOD" ? "good" : "attention"}>{status==="GOOD" ? "정상" : "확인"}</span>
      <div><strong>{label}</strong><small>{detail}</small></div>
    </div>
  );
}
